import React, { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import ConfigurationApiTable from '../configurationApiTable';
import { Box } from '@mui/material';
import { fetchAllCompetitors, matchCompetitorsByAi, mergeCompetitors } from '../../../../../api/competitors';
import ConfirmationDialog from '../../../../common/confirmationDialog';
import { getAllCompetitionsByLeague } from '../../../../../api/competitions';
import MatchAiDialog from '../matchAiDialog/MatchAiDialog';

const { v4: uuidv4 } = require('uuid');

const ConfigurationApiTeams = ({ sport, sources, onClose }) => {
  const [teamsHashBySource, setTeamsHashBySource] = useState({});
  const [apiConfigurationHash, setApiConfigurationHash] = useState({});
  const [competitions, setCompetitions] = useState([]);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [confirmationDialogData, setConfirmationDialogData] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  const createTeamsHash = useCallback((teams) => {
    const hash = teams.reduce((a, c) => {
      if (!a.teamsHashBySource[c.source]) {
        a.teamsHashBySource[c.source] = {};
      }
      a.teamsHashBySource[c.source][c.id] = c;

      if (!c.unionKey) {
        return a;
      }
      if (a.apiConfigurationHash[c.unionKey]) {
        a.apiConfigurationHash[c.unionKey][c.source] = { id: c.id, source: c.source, unionKey: c.unionKey };
      } else {
        a.apiConfigurationHash[c.unionKey] = { [c.source]: { id: c.id, source: c.source, unionKey: c.unionKey } };
      }

      return a;
    }, { teamsHashBySource: {}, apiConfigurationHash: {} });
    setTeamsHashBySource(hash.teamsHashBySource);
    setApiConfigurationHash(hash.apiConfigurationHash);
  }, []);

  const setupTeams = useCallback(async () => {
    if (!sources) {
      return;
    }
    const sourceIdParams = sources.map((item) => ({ key: 'sources', value: item.id }));
    const params = [
      {
        key: "sportId",
        value: sport.id,
      },
      {
        key: 'take',
        value: 10000
      },
      ...sourceIdParams
    ];
    const [teams, competitions] = await Promise.all([fetchAllCompetitors(params), getAllCompetitionsByLeague(params)]);
    createTeamsHash(teams.data);
    setCompetitions(competitions.filter((item) => item.unionKey));
  }, [sport, sources, createTeamsHash, setCompetitions]);

  const handleAddRow = useCallback(() => {
    const id = uuidv4();
    const items = {};
    sources.forEach((source) => items[source.id] = { id: null, source: source.id, unionKey: id });
    setApiConfigurationHash({ ...apiConfigurationHash, [id]: items });
  }, [apiConfigurationHash, sources]);

  const handleDeleteRow = useCallback((unionKey) => {
    const configuration = { ...apiConfigurationHash };
    delete configuration[unionKey];
    setApiConfigurationHash(configuration);
  }, [apiConfigurationHash]);

  const handleChange = useCallback((unionKey, source, team) => {
    const updatedItem = { ...apiConfigurationHash[unionKey][source], id: team?.id || null };
    setApiConfigurationHash({ ...apiConfigurationHash, [unionKey]: { ...apiConfigurationHash[unionKey], [source]: updatedItem } });
  }, [apiConfigurationHash]);

  const createMatchedItemsHashById = useCallback(() => {
    const matchedItems = {};
    for (let key in apiConfigurationHash) {
      const currentData = apiConfigurationHash[key];
      for (let sourceKey in currentData) {
        const sourceData = currentData[sourceKey];
        if (sourceData.id !== null && sourceData.unionKey !== null) {
          matchedItems[sourceData.id] = sourceData.unionKey;
        }
      }
    }
    return matchedItems;
  }, [apiConfigurationHash]);

  const handleSave = useCallback(async () => {
    const data = [];
    for (let key in apiConfigurationHash) {
      const currentData = apiConfigurationHash[key];
      const dataArray = [];
      for (let sourceKey in currentData) {
        const sourceData = currentData[sourceKey];
        if (sourceData.id !== null) {
          dataArray.push(sourceData);
        }
      }
      if (dataArray.length > 0) {
        data.push(dataArray);
      }
    }
    await mergeCompetitors(sport.id, { items: data });
    toast.success('API Configuration saved successfully!');
  }, [apiConfigurationHash, sport.id]);

  const handleCloseDialog = useCallback(() => {
    setIsConfirmationDialogOpen(false);
    setConfirmationDialogData(null);
  }, []);

  const handleCloseClick = useCallback(() => {
    setIsConfirmationDialogOpen(true);
    setConfirmationDialogData({
      title: 'Confirm close',
      content: 'Are you sure you want to close? Changes won`t be saved.',
      onConfirm: () => {
        onClose();
        handleCloseDialog();
      },
      onClose: () => handleCloseDialog(),
    })
  }, [handleCloseDialog, onClose]);

  const validateAiRow = useCallback((row) => {
    if (row.items.length > sources.length) {
      return false;
    }
    const sourcesAmount = {};
    for (let i = 0; i < row.items.length; i++) {
      const item = row.items[i];
      if (!teamsHashBySource[item?.source][item?.id]) {
        return false;
      }
      if (!sourcesAmount[item.source]) {
        sourcesAmount[item.source] = 1;
      } else {
        sourcesAmount[item.source] += 1;
      }
      if (sourcesAmount[item.source] > 1) {
        return false;
      }
    }
    return true;
  }, [teamsHashBySource, sources]);

  const handleHashChange = useCallback((matchedItems) => {
    const matchedRows = { ...apiConfigurationHash };
    const matchedItemsHashById = createMatchedItemsHashById();
    matchedItems.forEach((row) => {
      const isValid = validateAiRow(row);
      if (!isValid) {
        return;
      }
      const existingUnionKey = row.items.find((item) => matchedItemsHashById[item.id]?.unionKey || null);
      for (const item of row.items) {
        if (matchedItemsHashById[item.id]) {
          delete matchedRows[matchedItemsHashById[item.id]];
        }
      }
      const id = uuidv4();
      const unionKey = existingUnionKey || id;
      matchedRows[unionKey] = {};
      row.items.forEach((item) => {
        matchedRows[unionKey][item.source] = {
          id: item.id,
          source: item.source,
          unionKey: unionKey,
          unionType: 'Ai',
          accuracy: row.accuracy
        };
        matchedItemsHashById[item.id] = unionKey;
      });
      sources.forEach((source) => {
        if (!matchedRows[unionKey][source.id]) {
          matchedRows[unionKey][source.id] = {
            id: null,
            source: source.id,
            unionKey: unionKey,
            unionType: null,
          }
        }
      });
    });
    setApiConfigurationHash(matchedRows);
    setAiDialogOpen(false);
  }, [apiConfigurationHash, sources, validateAiRow, createMatchedItemsHashById]);

  useEffect(() => {
    setupTeams();
  }, [setupTeams]);

  return (
    <Box sx={{ height: 'calc(100vh - 275px)' }}>
      {confirmationDialogData && <ConfirmationDialog open={isConfirmationDialogOpen} title={confirmationDialogData.title} onClose={confirmationDialogData.onClose} onConfirm={confirmationDialogData.onConfirm} content={confirmationDialogData.content} />}
      {aiDialogOpen && <MatchAiDialog open={aiDialogOpen} items={competitions} sport={sport.id} onChange={handleHashChange} onClose={() => setAiDialogOpen(false)} fetchFunc={matchCompetitorsByAi} payloadFieldName={'competitionUnionKey'} />}
      <ConfigurationApiTable
        sources={sources}
        itemsHashBySource={teamsHashBySource}
        apiConfigurationHash={apiConfigurationHash}
        onChange={handleChange}
        onDeleteRow={handleDeleteRow}
        autocompleteLabel={'Team'}
        onAddRow={handleAddRow}
        onAiMatch={() => setAiDialogOpen(true)}
        onSave={handleSave}
        onClose={handleCloseClick}
        showFilter
      />
    </Box>
  )
}

export default ConfigurationApiTeams;
